import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ButtonWithIcon } from '@els/els-react--button';
import { ValidatedTextInput } from '@els/els-react--text-input';
import Auth from '@Services/Auth';
import AppStorage from '@Services/AppStorage';
import history from '@AppRoutes/history';
import { generatePath } from 'react-router';
import { paths } from '@AppRoutes/constants';
import ConfirmationPopup from '@Pages/History/ConfirmationPopup';
import { trackConversionDriverClick, trackHistoryEditNameButtonClick } from '../../analytics';

function InlineRename({ name, isEditing, editValue, onStartEdit, onCancelEdit, onSave, onEditValueChange, item }) {
  const wasEditingRef = useRef(isEditing);
  const trimmedValue = editValue.trim();
  const isSaveDisabled = trimmedValue === '' || trimmedValue.length < 3 || trimmedValue.length > 50;
  const errorTitle = 'Login required';
  const errorMessage = 'Please sign-in/register to save your History.';
  const inputLabel = `Rename History Item for ${name}`;

  useEffect(() => {
    if (isEditing) {
      const renameInput = document.querySelector('[data-e2e="rename-input"]');
      renameInput?.focus();
    } else if (wasEditingRef.current) {
      const renameButton = document.querySelector('[data-e2e="rename-edit-button"]');
      renameButton?.focus({ preventScroll: true });
    }

    wasEditingRef.current = isEditing;
  }, [isEditing]);

  const signIn = () => {
    history.push({ pathname: generatePath(paths.LOGIN) });
  };
  const handleConirmSignIn = (handleSignIn) => {
    onCancelEdit();
    handleSignIn();
  };

  if (isEditing && Auth.isAnonymousUser()) {
    return (
      <ConfirmationPopup
        title={errorTitle}
        text={errorMessage}
        submitButtonText="Sign in"
        dismissButtonText="Cancel"
        isCancelButton
        onClose={onCancelEdit}
        onConfirm={() => {
          trackConversionDriverClick('history:savesearch:sign-in');
          AppStorage.instance.set('scheduledHistory', item);
          handleConirmSignIn(signIn);
        }}
        closed={false}
        selfClose={null}
      />
    );
  }

  return (
    <div className="u-els-display-flex u-els-flex-direction-column">
      {isEditing ? (
        <form
          className="rx-align-center u-els-width-100 u-els-margin-right-1o2"
          aria-label={`Rename saved search ${name}`}
          onSubmit={(event) => {
            event.preventDefault();
            if (!isSaveDisabled) {
              onSave();
            }
          }}>
          <ValidatedTextInput
            data-e2e="rename-input"
            name="renameInput"
            className="u-els-flex-1"
            value={editValue}
            min={3}
            max={50}
            validator={ValidatedTextInput.Validators.NONE}
            onlyValidateWhenTouched
            label={inputLabel}
            required
            showRequired
            shouldHideLabel
            aria-label={inputLabel}
            aria-required="true"
            onChange={(e) => onEditValueChange(e.target?.value)}
          />
          <ButtonWithIcon
            data-e2e="rename-save-button"
            className="u-els-margin-left-1o2"
            sprite="Checkmark"
            iconOnly
            size="small"
            type="link"
            aria-label={`Save renamed search ${name}`}
            disabled={isSaveDisabled}
            onClick={onSave}>
            Save
          </ButtonWithIcon>
        </form>
      ) : (
        <div className="u-els-display-flex">
          <h3 className="u-els-font-size-h4">{name}</h3>
          <ButtonWithIcon
            data-e2e="rename-edit-button"
            className="u-els-margin-left-1o2"
            sprite="Edit"
            iconOnly
            size="small"
            type="link"
            aria-label={`Rename saved search ${name}`}
            onClick={() => {
              trackHistoryEditNameButtonClick();
              onStartEdit();
            }}>
            Rename
          </ButtonWithIcon>
        </div>
      )}
    </div>
  );
}

InlineRename.propTypes = {
  name: PropTypes.string.isRequired,
  isEditing: PropTypes.bool.isRequired,
  editValue: PropTypes.string.isRequired,
  onStartEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onEditValueChange: PropTypes.func.isRequired,
  item: PropTypes.object,
};

export default InlineRename;
